import { FC, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import type { Container, BatteryType } from '@/hooks/useBatteryContainers';

const CAPACITY_OPTIONS = [16, 18, 20, 22, 24, 26, 28, 30];

interface ContainerModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (
    container: Omit<Container, 'id' | 'created_at'>,
    addBatteries?: { batteryTypeId: string; quantity: number }
  ) => Promise<any>;
  batteryTypes: BatteryType[];
}

export const ContainerModal: FC<ContainerModalProps> = ({ open, onClose, onCreate, batteryTypes }) => {
  const [name, setName] = useState('');
  const [arrivalDate, setArrivalDate] = useState('');
  const [location, setLocation] = useState('');
  const [capacity, setCapacity] = useState<number>(16);
  const [max50kw, setMax50kw] = useState<number>(16);
  const [max100kw, setMax100kw] = useState<number>(8);
  const [status, setStatus] = useState<Container['status']>('onderweg');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Battery options
  const [addBatteries, setAddBatteries] = useState(false);
  const [selectedBatteryTypeId, setSelectedBatteryTypeId] = useState('');
  const [batteryQuantity, setBatteryQuantity] = useState('1');

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error('Naam is verplicht');
      return;
    }

    // Validate battery options if enabled
    if (addBatteries) {
      if (!selectedBatteryTypeId) {
        toast.error('Selecteer een batterijtype');
        return;
      }
      
      const qty = parseInt(batteryQuantity);
      if (!qty || qty < 1) {
        toast.error('Aantal batterijen moet minimaal 1 zijn');
        return;
      }

      const selectedType = batteryTypes.find(t => t.id === selectedBatteryTypeId);
      if (selectedType) {
        const slotsNeeded = selectedType.capacity_kwh === 100 ? qty * 2 : qty;
        if (slotsNeeded > capacity) {
          toast.error(`Niet genoeg ruimte. ${qty} x ${selectedType.capacity_kwh}kWh batterijen hebben ${slotsNeeded} posities nodig, maar container heeft ${capacity} posities.`);
          return;
        }
      }
    }

    setLoading(true);
    try {
      const batteryOptions = addBatteries && selectedBatteryTypeId
        ? { batteryTypeId: selectedBatteryTypeId, quantity: parseInt(batteryQuantity) }
        : undefined;

      await onCreate({
        name: name.trim(),
        arrival_date: arrivalDate || null,
        location: location.trim() || null,
        capacity,
        max_batteries_50kw: max50kw,
        max_batteries_100kw: max100kw,
        status,
        notes: notes.trim() || null,
      }, batteryOptions);
      
      toast.success('Container succesvol aangemaakt');
      handleClose();
    } catch (error) {
      console.error('Error creating container:', error);
      toast.error('Fout bij aanmaken container');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setArrivalDate('');
    setLocation('');
    setCapacity(16);
    setMax50kw(16);
    setMax100kw(8);
    setStatus('onderweg');
    setNotes('');
    setAddBatteries(false);
    setSelectedBatteryTypeId('');
    setBatteryQuantity('1');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Nieuwe Container Toevoegen</DialogTitle>
          <DialogDescription>
            Voeg een nieuwe batterijcontainer toe aan de voorraad
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">Naam *</Label>
            <Input
              id="name"
              placeholder="Bijv. Container 001"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="arrival">Verwachte Aankomstdatum</Label>
            <Input
              id="arrival"
              type="date"
              value={arrivalDate}
              onChange={(e) => setArrivalDate(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">Locatie</Label>
            <Input
              id="location"
              placeholder="Bijv. Warehouse A"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="max50kw">Max 50kW Batterijen</Label>
              <Input
                id="max50kw"
                type="number"
                min="0"
                value={max50kw}
                onChange={(e) => setMax50kw(parseInt(e.target.value) || 0)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="max100kw">Max 100kW Batterijen</Label>
              <Input
                id="max100kw"
                type="number"
                min="0"
                value={max100kw}
                onChange={(e) => setMax100kw(parseInt(e.target.value) || 0)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select value={status} onValueChange={(value) => setStatus(value as Container['status'])}>
              <SelectTrigger id="status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="onderweg">Onderweg</SelectItem>
                <SelectItem value="aangekomen">Aangekomen</SelectItem>
                <SelectItem value="in_gebruik">In Gebruik</SelectItem>
                <SelectItem value="leeg">Leeg</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notities</Label>
            <Textarea
              id="notes"
              placeholder="Optionele notities..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          <div className="border-t pt-4 space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="add-batteries"
                checked={addBatteries}
                onCheckedChange={(checked) => setAddBatteries(checked as boolean)}
              />
              <Label htmlFor="add-batteries" className="font-normal cursor-pointer">
                Direct batterijen toevoegen aan container
              </Label>
            </div>

            {addBatteries && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="battery-type">Batterijtype</Label>
                  <Select value={selectedBatteryTypeId} onValueChange={setSelectedBatteryTypeId}>
                    <SelectTrigger id="battery-type">
                      <SelectValue placeholder="Selecteer type" />
                    </SelectTrigger>
                    <SelectContent>
                      {batteryTypes.map(type => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name} ({type.capacity_kwh}kWh / {type.power_kw}kW)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="battery-quantity">Aantal batterijen</Label>
                  <Input
                    id="battery-quantity"
                    type="number"
                    min="1"
                    max={selectedBatteryTypeId 
                      ? (batteryTypes.find(t => t.id === selectedBatteryTypeId)?.capacity_kwh === 100 
                          ? Math.floor(capacity / 2) 
                          : capacity)
                      : capacity
                    }
                    value={batteryQuantity}
                    onChange={(e) => setBatteryQuantity(e.target.value)}
                  />
                  {selectedBatteryTypeId && (
                    <p className="text-xs text-muted-foreground">
                      {batteryTypes.find(t => t.id === selectedBatteryTypeId)?.capacity_kwh === 100
                        ? `Max ${Math.floor(capacity / 2)} x 100kWh batterijen (nemen elk 2 posities in)`
                        : `Max ${capacity} x 50kWh batterijen`}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Annuleren
          </Button>
          <Button onClick={handleCreate} disabled={loading}>
            {loading ? 'Aanmaken...' : 'Container Toevoegen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
